import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { brand } from '../config'
import { useCustomer } from '../context/CustomerContext'
import { useProducts } from '../context/ProductsContext'
import { customerApi } from '../lib/customerApi'

/**
 * ♡ Save-to-wishlist button for product pages. Signed-out visitors are
 * sent to sign-in; saved items get back-in-stock and price-drop emails.
 */
export default function WishlistButton({ productId, variantId }) {
  const navigate = useNavigate()
  const { customer } = useCustomer()
  const { customerAccounts } = useProducts()
  const [items, setItems] = useState(null)
  const [busy, setBusy] = useState(false)

  const wishlistEnabled = customerAccounts?.enabled !== false && customerAccounts?.features?.wishlist !== false

  useEffect(() => {
    if (!customer || !wishlistEnabled) { setItems(null); return }
    customerApi.getWishlist().then((r) => setItems(r.items ?? [])).catch(() => setItems([]))
  }, [customer, wishlistEnabled])

  if (!wishlistEnabled) return null

  const saved = (items ?? []).find((i) => i.productId === productId && String(i.variantId) === String(variantId))

  async function toggle() {
    if (!customer) { navigate('/account/login'); return }
    if (busy || items === null) return
    setBusy(true)
    try {
      const r = saved
        ? await customerApi.removeFromWishlist(saved.id)
        : await customerApi.addToWishlist(productId, variantId)
      setItems(r.items ?? [])
    } catch { /* leave state as-is */ }
    finally { setBusy(false) }
  }

  return (
    <button
      type="button"
      onClick={toggle}
      title={customer ? (saved ? 'Remove from wishlist' : 'Save to wishlist') : 'Sign in to save to your wishlist'}
      aria-label={saved ? 'Remove from wishlist' : 'Save to wishlist'}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
        minHeight: '44px', padding: '0 18px',
        background: saved ? 'rgba(248,113,113,0.10)' : 'transparent',
        border: `0.5px solid ${saved ? 'rgba(248,113,113,0.5)' : brand.colors.border}`,
        borderRadius: brand.radii.md, cursor: 'pointer',
        color: saved ? '#f87171' : brand.colors.textPrimary,
        fontSize: '15px', fontWeight: 600, fontFamily: brand.fonts.body,
        transition: 'border-color 0.2s ease, background 0.2s ease',
        opacity: busy ? 0.6 : 1,
      }}
    >
      <svg width="17" height="17" viewBox="0 0 24 24" fill={saved ? '#f87171' : 'none'}
        stroke={saved ? '#f87171' : 'currentColor'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
      </svg>
      {saved ? 'Saved' : 'Save'}
    </button>
  )
}
